import React, { useState } from "react";
import { Box, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useTranslation } from "react-i18next";

interface SyncErrorBadgeProps {
  error?: string | null;
}

const SyncErrorBadge: React.FC<SyncErrorBadgeProps> = ({ error }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [copied, setCopied] = useState(false);

  if (!error) {
    return (
      <Typography variant="pSmallest" color="text.secondary">
        -
      </Typography>
    );
  }

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e?: {} & Partial<React.SyntheticEvent>) => {
    e?.stopPropagation?.();
    setAnchorEl(null);
    setCopied(false);
  };

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(error);
      setCopied(true);
    } catch {
      // Clipboard access can fail (permissions/unsupported); badge still shows the error text.
    }
  };

  return (
    <>
      <Box
        component="span"
        onClick={handleOpen}
        sx={{
          color: theme.palette.error.main,
          fontWeight: 700,
          fontSize: 12,
          fontFamily: "Nunito",
          letterSpacing: 0.5,
          cursor: "pointer",
          textDecoration: "underline",
          textUnderlineOffset: "2px",
        }}
      >
        ERROR
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ p: 2, maxWidth: 420, minWidth: 280 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              mb: 1,
            }}
          >
            <Typography
              variant="pSmall"
              sx={{ fontWeight: 700, color: theme.palette.error.main }}
            >
              {t("campaigns.errorSync", { defaultValue: "Sync Error" })}
            </Typography>
            <Tooltip
              title={
                copied
                  ? t("common.copied", { defaultValue: "Copied!" })
                  : t("common.copy", { defaultValue: "Copy" })
              }
            >
              <IconButton size="small" onClick={handleCopy}>
                {copied ? (
                  <CheckIcon fontSize="small" color="success" />
                ) : (
                  <ContentCopyIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            variant="pSmallest"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              display: "block",
              maxHeight: 320,
              overflowY: "auto",
            }}
          >
            {error}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};

export default SyncErrorBadge;
